import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axiosInstance from "../utils/axiosInstance";
import { API_URL } from "../utils/runtimeConfig";
import { AppButton, PageShell, SectionCard } from "../components/ui";
import "./css/VoteScreen.css";

const apiUrl = API_URL;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const VoteScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const axios = axiosInstance;

  const [voteData, setVoteData] = useState(null);
  const [playerName, setPlayerName] = useState("");
  const [selection, setSelection] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    const hasVoted = localStorage.getItem(`votedOn${id}`);
    if (hasVoted) {
      setErrorText("It looks like you already voted on this poll.");
      navigate("/");
    }
  }, [id, navigate]);

  useEffect(() => {
    const fetchVote = async () => {
      try {
        const response = await axios.get(`${apiUrl}/votes/${id}/cast`);
        setVoteData(response.data);
      } catch (error) {
        console.error("Error fetching vote data:", error);
        setErrorText("Could not load vote details.");
      }
    };

    fetchVote();
  }, [id, axios]);

  const handleTimeSelectionChange = (dayIndex, hour) => {
    const timeSlot = `${dayIndex}-${hour}`;
    setSelection((prevSelection) =>
      prevSelection.includes(timeSlot)
        ? prevSelection.filter((item) => item !== timeSlot)
        : [...prevSelection, timeSlot]
    );
  };

  const handleCustomSelectionChange = (option) => {
    setSelection((prevSelection) => {
      if (prevSelection.includes(option)) {
        return prevSelection.filter((item) => item !== option);
      }
      if (prevSelection.length < voteData.pickLimit) {
        return [...prevSelection, option];
      }
      return prevSelection;
    });
  };

  const handleSubmit = async () => {
    if (!playerName.trim()) {
      setErrorText("Please enter your name.");
      return;
    }

    setErrorText("");
    setIsSubmitting(true);
    try {
      await axios.put(`${apiUrl}/votes/${id}/cast`, {
        name: playerName,
        selection,
      });
      localStorage.setItem(`votedOn${id}`, "true");
      navigate("/");
    } catch (error) {
      console.error("Error casting vote:", error);
      setErrorText("Error submitting your vote. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!voteData) {
    return (
      <PageShell maxWidth="lg">
        <SectionCard>
          <Typography>Loading vote details...</Typography>
        </SectionCard>
      </PageShell>
    );
  }

  const restrictedTimeSlots = new Set(voteData.restrictedTimeSlots || []);

  return (
    <PageShell maxWidth="lg" className="vote-screen-shell">
      <SectionCard className="vote-screen">
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {voteData.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cast your vote below.
            </Typography>
          </Box>

          {errorText && <Alert severity="warning">{errorText}</Alert>}

          <TextField
            label="Your name"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="Enter your name"
            fullWidth
          />

          {voteData.type === "time_selection" && (
            <TableContainer component={Paper} variant="outlined" className="time-selection">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    {DAYS.map((day) => (
                      <TableCell key={day} align="center">
                        {day}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(24).keys()].map((hour) => (
                    <TableRow key={hour}>
                      <TableCell>{`${hour.toString().padStart(2, "0")}:00`}</TableCell>
                      {DAYS.map((_, dayIndex) => {
                        const slot = `${dayIndex}-${hour}`;
                        return (
                          <TableCell key={slot} align="center">
                            <Checkbox
                              checked={selection.includes(slot)}
                              onChange={() => handleTimeSelectionChange(dayIndex, hour)}
                              disabled={!restrictedTimeSlots.has(slot)}
                              size="small"
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {voteData.type === "custom_selection" && (
            <Box className="custom-selection">
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Pick up to {voteData.pickLimit} option(s):
              </Typography>
              <Stack spacing={0.5}>
                {voteData.customOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={selection.includes(option)}
                        onChange={() => handleCustomSelectionChange(option)}
                        disabled={!selection.includes(option) && selection.length >= voteData.pickLimit}
                      />
                    }
                    label={option}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <AppButton
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{ minWidth: 220 }}
            >
              {isSubmitting ? "Submitting..." : "Cast Vote"}
            </AppButton>
          </Box>
        </Stack>
      </SectionCard>
    </PageShell>
  );
};

export default VoteScreen;
